import { Component, OnDestroy, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { SharedService } from '../shared/services/shared.service';
import { ModalService } from '../shared/services/modal.service';
import { CalendarService } from './services/calendar.service';
import { BehaviorSubject, Subject, takeUntil, filter, take } from 'rxjs';
import { Questions } from '../shared/models/constants';


@Component({
  selector: 'app-calendar',
  standalone: true,
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.css',
  imports: [FullCalendarModule, AsyncPipe],
})
export class CalendarComponent implements OnDestroy, OnInit {
  // Context Menu State
  private menuStateSubject = new BehaviorSubject<{
    showMenu: boolean;
    menuTop: number;
    menuLeft: number;
    date: Date | null;
    event: any | null;
  }>({
    showMenu: false,
    menuTop: 0,
    menuLeft: 0,
    date: null,
    event: null
  });
  menuState$ = this.menuStateSubject.asObservable();

  selectedDate: Date | null = null;

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    plugins: [dayGridPlugin, interactionPlugin],
    customButtons: {
      refresh: {
        text: 'Refresh',
        click: () => this.refreshCalendar()
      }
    },
    headerToolbar: {
      left: 'prev,next today refresh',
      center: 'title',
      right: 'dayGridMonth,dayGridWeek,dayGridDay'
    },
    editable: true,
    selectable: true,
    selectMirror: true,
    unselectAuto: false,
    navLinks: true, 
    select: (selectionInfo) => {
      this.selectedDate = selectionInfo.start;
      // Sync the calendar's focus date so switching views works
      selectionInfo.view.calendar.gotoDate(selectionInfo.start);
      // Trigger re-render to apply the highlight class
      this.calendarOptions = { ...this.calendarOptions };
    },
    dayCellClassNames: (arg) => {
      if (this.selectedDate && arg.date.getTime() === this.selectedDate.getTime()) {
        return ['selected-day-highlight'];
      }
      return [];
    },
    selectAllow: (selectInfo) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectInfo.start >= today;
    },
    dayMaxEvents: true,
    height: 700,
    // Add right-click listener to each day cell
    dayCellDidMount: (info) => {
      info.el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        this.onContextMenu(info.date, e);
      });
    },
    // Add right-click listener to each rendered event
    eventDidMount: (info) => {
      // Right‑click for context menu
      info.el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onEventContextMenu(info.event, e as MouseEvent);
      });
      // Double‑click to edit the event
      info.el.addEventListener('dblclick', () => {
        this.modalService.openAddEditEventModal(info.event.start, info.event);
      });
    },
    eventDrop: (info) => this.handleEventChange(info),
    eventResize: (info) => this.handleEventChange(info),
  };

  async handleEventChange(changeInfo: any) {
    const event = changeInfo.event;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (event.start < today) {
      this.sharedService.raiseErrorToast('Cannot move events to the past');
      changeInfo.revert();
      return;
    }

    const payload = {
      title: event.title,
      start: event.start.toISOString(),
      end: event.end ? event.end.toISOString() : new Date(event.start.getTime() + 30 * 60000).toISOString(),
      customer_id: event.extendedProps?.customer_id ?? null,
      description: event.extendedProps?.description ?? ''
    };

    if (event.id) {
      await this.calendarService.updateEvent(event.id, payload);
      this.sharedService.raiseSuccessToast('Event updated successfully');
    }
  }

  constructor(
    private sharedService: SharedService,
    private modalService: ModalService,
    private calendarService: CalendarService
  ) {
    // Listen for events and update the calendar
    this.sharedService.calendar_events$
      .pipe(takeUntil(this.destroy$))
      .subscribe(events => {
        this.calendarOptions = {
          ...this.calendarOptions,
          events: events
        };
      });
  }

  async ngOnInit() {
    // Listen for company info being retrieved/updated
    this.sharedService.company_info_retrieved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshCalendar();
      });

    // Initial load if company info is already present
    if (this.sharedService.company_info?.company_id) {
      this.refreshCalendar();
    }
  }

  destroy$ = new Subject<void>();

  onContextMenu(date: Date, event: MouseEvent) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return;

    this.menuStateSubject.next({
      showMenu: true,
      menuTop: event.clientY,
      menuLeft: event.clientX,
      date: date,
      event: null
    });
  }

  onEventContextMenu(calEvent: any, mouseEvent: MouseEvent) {
    this.menuStateSubject.next({
      showMenu: true,
      menuTop: mouseEvent.clientY,
      menuLeft: mouseEvent.clientX,
      date: calEvent.start,
      event: calEvent
    });
  }

  closeMenu() {
    this.menuStateSubject.next({
      ...this.menuStateSubject.value,
      showMenu: false
    });
  }

  addEventFromMenu() {
    const date = this.menuStateSubject.value.date;
    this.closeMenu();
    this.modalService.openAddEditEventModal(date);
  }

  editEventFromMenu() {
    const event = this.menuStateSubject.value.event;
    this.closeMenu();
    this.modalService.openAddEditEventModal(event?.start, event);
  }

  async deleteEventFromMenu() {
    const event = this.menuStateSubject.value.event;
    this.closeMenu();
    
    if (!event || !event.id) return;

    this.modalService.openYesNoModal(Questions.delete_event);

    this.sharedService.yes_no_submit$
      .pipe(
        filter(res => res.question === Questions.delete_event),
        take(1)
      )
      .subscribe(async (res) => {
        if (res.answer === 'Yes') {
          try {
            await this.calendarService.deleteEvent(event.id);
            this.sharedService.raiseSuccessToast('Event deleted');
            await this.refreshCalendar();
          } catch (error) {
            this.sharedService.raiseErrorToast('Failed to delete event');
          }
        }
      });
  }

  openHome() {
    this.sharedService.changeRoute('home');
  }

  async refreshCalendar(): Promise<void> {
    const companyId = this.sharedService.company_info?.company_id;
    if (!companyId) return;

    // Wrap in setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.sharedService.loading = true;
      this.sharedService.loading_message = 'Refreshing Calendar...';
    });
    try {
      // Re-fetch events from backend (or IndexedDB) and push to shared service
      const events = await this.calendarService.getEvents();
      this.sharedService.calendar_events$.next(
        events.map(e => ({
          id: e.id,
          title: e.title,
          start: e.start,
          end: e.end,
          extendedProps: { 
            customer_id: e.customer_id, 
            description: e.description,
            send_reminder: e.send_reminder
          }
        }))
      );
    } finally {
      this.sharedService.loading = false;
    }
  }

  // Close menu on any click
  onWindowClick() {
    if (this.menuStateSubject.value.showMenu) {
      this.closeMenu();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
